import {Answer, PersistedGuess} from '../models/answer.model';
import {Question} from '../models/game-data.model';
import {GameState, QuestionState} from '../models/game-models.model';

/**
 * A question enriched with its position within the current game.
 */
export type CurrentQuestion = Question & {index: number};

/**
 * The complete, flat state held by the {@link GameStore}.
 */
export type GameStoreState = {
  /** The overall game phase. */
  phase: GameState;
  /** The theme the current game is played with, or `null` for a random game. */
  themeId: number | null;
  /** Index of the current question within {@link currentGameQuestions}. */
  currentQuestionIndex: number;
  /** State of the question that is currently being played. */
  questionState: QuestionState;
  /** The questions that make up the current game. */
  currentGameQuestions: Question[];
  /** States of the questions that have already been finished this game. */
  finishedQuestionStates: QuestionState[];
  /**
   * Keys of questions already seen, per theme (`'all'` for the no-theme game). Stored as
   * {@link questionKey} identifiers rather than full question copies to keep the persisted
   * state small; the drawable pool is derived as (all of the theme's questions) minus the
   * questions whose key appears here.
   */
  previousGamesQuestions: Record<string, string[]>;
  /** Whether the answer layer is currently shown on the map. Resets to `false` on every question change. */
  showAnswerOnMap: boolean;
  /** The name of the correct answer feature for the current question. */
  answer: string;
  /** Legend url of the currently shown answer layer. */
  answerLegendUrl: string;
  /** The guesses made for the current question. */
  guesses: Answer[];
  /**
   * Serialisable projection of {@link guesses} for the current question,
   * persisted across refreshes so the map can rehydrate them once its features
   * have loaded.
   */
  persistedGuesses: PersistedGuess[];
  /** The remaining answer options for the current question. */
  answerOptions: Answer[];
  /** The final score, computed at game over. */
  score: number;
  /** The latest error message, or `undefined` when there is no error. */
  error: string | undefined;
  /** Whether a game-instructions modal (`gm-modal`) is currently open. Used to hide overlays, like the map legend, that would otherwise render on top of it. */
  isInfoModalOpen: boolean;
};

/**
 * The pristine state of a single question at the start of a turn.
 */
export const startQuestionState: Readonly<QuestionState> = Object.freeze({
  attempt: 1,
  feedback: null,
  hasHadHint: false,
});

/**
 * Returns `true` when a question has reached a terminal feedback state
 * (either correctly answered or out of attempts).
 *
 * This replaces the previously stored `isCompleted` flag, which was always
 * derivable from `feedback`.
 */
export function isQuestionCompleted(questionState: Pick<QuestionState, 'feedback'> | null): boolean {
  return questionState !== null && (questionState.feedback === 'correct' || questionState.feedback === 'wrong-and-over');
}

/**
 * The initial {@link GameStoreState}.
 */
export const initialGameState: GameStoreState = {
  phase: 'starting',
  themeId: null,
  currentQuestionIndex: 0,
  questionState: {...startQuestionState},
  currentGameQuestions: [],
  finishedQuestionStates: [],
  previousGamesQuestions: {},
  showAnswerOnMap: false,
  answer: '',
  answerLegendUrl: '',
  guesses: [],
  persistedGuesses: [],
  answerOptions: [],
  score: 0,
  error: undefined,
  isInfoModalOpen: false,
};

/** The maximum number of attempts a player has per question. */
export const MAX_ATTEMPTS_PER_QUESTION = 3;
